/**
 * MarkdownContent — shared ReactMarkdown renderer with the platform's
 * chat-style typography. Extracted from the chatbot's MessageItem so the
 * agent runner (and future AI surfaces) render LLM answers identically
 * instead of growing per-feature style copies (Rule 15 / DRY).
 *
 * `variant="inverted"` is for content on dark/colored bubbles (the
 * chatbot's user messages); `default` is for light surfaces.
 *
 * @module MarkdownContent
 */

'use client';

import { type ReactNode, type AnchorHTMLAttributes, useMemo } from 'react';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Box } from '@axon/ui';

export type MarkdownVariant = 'default' | 'inverted';

const getMarkdownStyles = (variant: MarkdownVariant) => {
  const inverted = variant === 'inverted';
  return {
    '& p': { margin: 0 },
    '& p + p': { marginTop: '0.5em' },
    // LLM answers frequently emit `#`/`##` headings; un-styled these render
    // as the browser default (huge) inside an `sm` chat bubble. Down-scale to
    // fit the bubble while keeping a visible hierarchy.
    '& h1, & h2, & h3, & h4': {
      fontWeight: 'semibold',
      lineHeight: '1.3',
      marginTop: '0.75em',
      marginBottom: '0.25em',
    },
    '& h1': { fontSize: '1.15em' },
    '& h2': { fontSize: '1.05em' },
    '& h3': { fontSize: '1em' },
    '& h4': { fontSize: '0.9em' },
    '& h1:first-of-type, & h2:first-of-type, & h3:first-of-type, & h4:first-of-type': {
      marginTop: 0,
    },
    '& code': {
      background: inverted ? 'whiteAlpha.200' : 'gray.100',
      padding: '2px 6px',
      borderRadius: '4px',
      fontSize: '0.875em',
      fontFamily: 'mono',
    },
    '& pre': {
      background: inverted ? 'whiteAlpha.200' : 'gray.100',
      padding: '8px 12px',
      borderRadius: '6px',
      overflow: 'auto',
      marginTop: '0.5em',
      marginBottom: '0.5em',
      '& code': {
        background: 'none',
        padding: 0,
      },
    },
    '& ul, & ol': {
      paddingLeft: '1.5em',
      margin: '0.5em 0',
    },
    '& li': {
      marginTop: '0.25em',
    },
    '& a': {
      color: inverted ? 'blue.200' : 'blue.600',
      textDecoration: 'underline',
      _hover: {
        textDecoration: 'none',
      },
    },
    '& strong': {
      fontWeight: 'semibold',
    },
    '& em': {
      fontStyle: 'italic',
    },
    '& blockquote': {
      borderLeftWidth: '3px',
      borderLeftColor: inverted ? 'whiteAlpha.400' : 'gray.300',
      paddingLeft: '1em',
      marginLeft: 0,
      fontStyle: 'italic',
    },
    // GFM tables (LLM answers frequently emit pipe tables — chatbot agent
    // replies, data summaries). Without these they rendered as raw `| … |`.
    '& table': {
      borderCollapse: 'collapse',
      width: '100%',
      margin: '0.5em 0',
      fontSize: '0.95em',
      display: 'block',
      overflowX: 'auto',
    },
    '& th, & td': {
      borderWidth: '1px',
      borderColor: inverted ? 'whiteAlpha.300' : 'gray.200',
      padding: '4px 8px',
      textAlign: 'left',
      verticalAlign: 'top',
    },
    '& th': {
      fontWeight: 'semibold',
      background: inverted ? 'whiteAlpha.200' : 'gray.50',
    },
  };
};

interface MarkdownContentProps {
  content: string;
  variant?: MarkdownVariant;
  fontSize?: string;
  /** Custom renderers (e.g. the chatbot's entity-link paragraph override). */
  components?: Components;
}

/**
 * Default `a` renderer: forces external links to open in a new tab with
 * `rel="noopener noreferrer"` so LLM-emitted links don't expose `window.opener`
 * or replace the chat surface. Callers can still override `a` via `components`.
 */
function MarkdownLink({
  children,
  ...props
}: AnchorHTMLAttributes<HTMLAnchorElement> & { children?: ReactNode }) {
  return (
    <a {...props} target="_blank" rel="noopener noreferrer">
      {children}
    </a>
  );
}

export function MarkdownContent({
  content,
  variant = 'default',
  fontSize = 'sm',
  components,
}: MarkdownContentProps) {
  const styles = useMemo(() => getMarkdownStyles(variant), [variant]);
  const mergedComponents = useMemo<Components>(
    () => ({ a: MarkdownLink, ...components }),
    [components]
  );
  return (
    <Box css={styles} fontSize={fontSize} lineHeight="1.5">
      <ReactMarkdown remarkPlugins={[remarkGfm]} components={mergedComponents}>
        {content}
      </ReactMarkdown>
    </Box>
  );
}

export default MarkdownContent;
